/**
 * Web Push client helper (dashboard)
 * ─────────────────────────────────────────────────────────────────────────────
 * Thin wrapper over the browser Push API that:
 *   • checks support + permission
 *   • subscribes this browser via the service worker's PushManager
 *   • ships the subscription to the backend (/webpush/subscribe)
 *   • unsubscribes + tells the backend to prune
 *
 * The heavy lifting (showing notifications when the tab is closed) lives in the
 * service worker; this file is only the subscribe/unsubscribe handshake.
 * ───────────────────────────────────────────────────────────────────────────── */

import {
  apiGetVapidPublicKey,
  apiSubscribeWebPush,
  apiUnsubscribeWebPush,
} from "../api/index";

export const WEB_PUSH_STATE_EVENT = "lusuku:webpush-state";

/** Notify the rest of the dashboard when this browser's subscription changes. */
export function announceWebPushState(state) {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent(WEB_PUSH_STATE_EVENT, { detail: state || {} })
  );
}

/** Push + service worker support in this browser. */
export function isPushSupported() {
  return (
    typeof navigator !== "undefined" &&
    "serviceWorker" in navigator &&
    typeof window !== "undefined" &&
    "PushManager" in window &&
    "Notification" in window
  );
}

/** Current Notification permission ("granted" | "denied" | "default" | "unsupported"). */
export function getPermission() {
  if (typeof Notification === "undefined") return "unsupported";
  return Notification.permission;
}

/** Convert a base64url VAPID public key into the Uint8Array the API expects. */
function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) output[i] = raw.charCodeAt(i);
  return output;
}

/** Wait for the service worker to be ready (bounded, so callers never hang). */
async function getReadyRegistration(timeoutMs = 8000) {
  if (!("serviceWorker" in navigator)) return null;
  const ready = navigator.serviceWorker.ready;
  const timeout = new Promise((resolve) => setTimeout(() => resolve(null), timeoutMs));
  return Promise.race([ready, timeout]);
}

/** Read the server's authoritative Web Push readiness and public key. */
export async function getWebPushConfiguration() {
  const buildTimeKey = process.env.REACT_APP_VAPID_PUBLIC_KEY?.trim() || null;
  try {
    const { data } = await apiGetVapidPublicKey();
    return {
      configured: data?.configured === true && Boolean(data?.publicKey),
      enabled: data?.enabled === true,
      publicKey: data?.publicKey || buildTimeKey,
      reachable: true,
    };
  } catch (_e) {
    return {
      configured: false,
      enabled: false,
      publicKey: buildTimeKey,
      reachable: false,
    };
  }
}

function keysMatch(existingKey, expectedKey) {
  if (!existingKey || existingKey.byteLength !== expectedKey.byteLength) {
    return false;
  }
  const existing = new Uint8Array(existingKey);
  return expectedKey.every((byte, index) => byte === existing[index]);
}

/**
 * Subscribe this browser to Web Push and register it with the backend.
 * Prompts for Notification permission if not already decided.
 * @returns {Promise<{ok:boolean, reason?:string}>}
 */
export async function subscribeToWebPush() {
  if (!isPushSupported()) return { ok: false, reason: "unsupported" };

  // Check the server before prompting for browser permission.
  const configuration = await getWebPushConfiguration();
  if (!configuration.configured || !configuration.publicKey) {
    return {
      ok: false,
      reason: configuration.reachable ? "not_configured" : "server_unreachable",
    };
  }
  const applicationServerKey = urlBase64ToUint8Array(configuration.publicKey);

  // Ask for permission (no-op if already granted).
  let permission = getPermission();
  if (permission === "default") {
    permission = await Notification.requestPermission();
  }
  if (permission !== "granted") return { ok: false, reason: "denied" };

  const registration = await getReadyRegistration();
  if (!registration) return { ok: false, reason: "no_service_worker" };

  try {
    // Reuse a subscription only when it belongs to the current VAPID key.
    let subscription = await registration.pushManager.getSubscription();
    if (
      subscription &&
      !keysMatch(subscription.options?.applicationServerKey, applicationServerKey)
    ) {
      const staleEndpoint = subscription.endpoint;
      await subscription.unsubscribe();
      try {
        await apiUnsubscribeWebPush(staleEndpoint);
      } catch (_e) {
        /* server prune best-effort */
      }
      subscription = null;
    }
    if (!subscription) {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey,
      });
    }

    await apiSubscribeWebPush({
      subscription: subscription.toJSON(),
      userAgent: navigator.userAgent,
    });
    announceWebPushState({
      supported: true,
      permission: getPermission(),
      subscribed: true,
      configured: configuration.configured,
      enabled: configuration.enabled,
      serverReachable: configuration.reachable,
      staleSubscription: false,
    });
    return { ok: true };
  } catch (err) {
    return { ok: false, reason: err?.message || "subscribe_failed" };
  }
}

/**
 * Unsubscribe this browser and ask the backend to prune the record.
 * @returns {Promise<{ok:boolean}>}
 */
export async function unsubscribeFromWebPush() {
  if (!isPushSupported()) return { ok: false };
  const registration = await getReadyRegistration();
  if (!registration) return { ok: false };
  try {
    const subscription = await registration.pushManager.getSubscription();
    if (subscription) {
      const endpoint = subscription.endpoint;
      await subscription.unsubscribe();
      try {
        await apiUnsubscribeWebPush(endpoint);
      } catch (_e) {
        /* server prune best-effort */
      }
    }
    const configuration = await getWebPushConfiguration();
    announceWebPushState({
      supported: true,
      permission: getPermission(),
      subscribed: false,
      configured: configuration.configured,
      enabled: configuration.enabled,
      serverReachable: configuration.reachable,
      staleSubscription: false,
    });
    return { ok: true };
  } catch (_err) {
    return { ok: false };
  }
}

/** Snapshot for UI: is this browser currently subscribed? */
export async function getSubscriptionState() {
  const supported = isPushSupported();
  const permission = getPermission();
  const configuration = await getWebPushConfiguration();
  let subscribed = false;
  let staleSubscription = false;
  if (supported) {
    try {
      const registration = await getReadyRegistration(3000);
      const sub = registration && (await registration.pushManager.getSubscription());
      if (sub && configuration.configured && configuration.publicKey) {
        const expectedKey = urlBase64ToUint8Array(configuration.publicKey);
        staleSubscription = !keysMatch(
          sub.options?.applicationServerKey,
          expectedKey
        );
      }
      subscribed = Boolean(sub) && !staleSubscription;
    } catch (_e) {
      subscribed = false;
    }
  }
  return {
    supported,
    permission,
    subscribed,
    configured: configuration.configured,
    enabled: configuration.enabled,
    serverReachable: configuration.reachable,
    staleSubscription,
  };
}
